<template>
	<div class="tab-item pb-1">
		<!-- Menu Tabs -->
		<CategoryDropdown v-if="!tabView" :tab="stickerTab" @change="actions.setTab" />
		<SubNav v-if="tabView" :tab="stickerTab" @change="actions.setTab" />

		<!-- Search Box -->
		<SearchBox :reset="resetSearch" @onSearch="actions.onSearch" />

		<div
			:class="[
				currentTabData.type === StickerType.REWARD ? 'reward-tab' : 'sticker-tab mt-1',
				isScrolling && 'scrolling',
			]"
			:id="currentTabData.type === StickerType.REWARD ? 'reward-tab' : 'sticker-tab'">
			<!-- All Stickers -->
			<template v-if="!currentTabData.type">
				<Stickers
					:loading="loading"
					:search="searchQuery"
					:data="filteredStickers"
					@onPlaySound="actions.onPlaySound"
					@onStickerClick="events.onStickerClick"
					@onAddFavorite="events.onAddFavorite"
					@onDeleteFavorite="events.onDeleteFavorite" />
			</template>

			<!-- Favorite Stickers -->
			<template v-if="currentTabData.type === StickerType.FAVORITE">
				<Stickers
					:loading="loading"
					:search="searchQuery"
					:data="filteredStickers"
					@onPlaySound="actions.onPlaySound"
					@onStickerClick="events.onStickerClick"
					@onAddFavorite="events.onAddFavorite"
					@onDeleteFavorite="events.onDeleteFavorite" />
			</template>

			<!-- CHANNEL Stickers -->
			<template v-if="currentTabData.type === StickerType.CUSTOM">
				<Stickers
					:loading="loading"
					:search="searchQuery"
					:data="filteredStickers"
					@onPlaySound="actions.onPlaySound"
					@onStickerClick="events.onStickerClick"
					@onAddFavorite="events.onAddFavorite"
					@onDeleteFavorite="events.onDeleteFavorite" />
			</template>

			<!-- Placement Stickers -->
			<template v-if="currentTabData.type == StickerType.PLACEMENT">
				<Stickers
					:loading="loading"
					:search="searchQuery"
					:data="filteredStickers"
					@onPlaySound="actions.onPlaySound"
					@onStickerClick="events.onStickerClick"
					@onAddFavorite="events.onAddFavorite"
					@onDeleteFavorite="events.onDeleteFavorite" />
			</template>

			<!-- Sub Only Stickers -->
			<template v-if="currentTabData.type == StickerType.SUB_ONLY">
				<Stickers
					:loading="loading"
					:search="searchQuery"
					:data="filteredStickers"
					@onPlaySound="actions.onPlaySound"
					@onStickerClick="events.onStickerClick"
					@onAddFavorite="events.onAddFavorite"
					@onDeleteFavorite="events.onDeleteFavorite" />
			</template>

			<!-- Power Stickers -->
			<template v-if="currentTabData.type == StickerType.POWER">
				<Stickers
					:stickerType="StickerType.POWER"
					:loading="loading"
					:search="searchQuery"
					:data="filteredStickers"
					@onPlaySound="actions.onPlaySound"
					@onStickerClick="events.onStickerClick"
					@onAddFavorite="events.onAddFavorite"
					@onDeleteFavorite="events.onDeleteFavorite" />
			</template>

			<!-- Reward Stickers -->
			<template v-if="currentTabData.type == StickerType.REWARD">
				<Stickers
					:stickerType="StickerType.REWARD"
					:search="searchQuery"
					:data="filteredStickers"
					:loading="loading"
					@onStickerClick="events.onStickerClick" />
			</template>

			<!-- TTS Stickers -->
			<template v-if="currentTabData.type == StickerType.TTS">
				<Stickers
					:stickerType="StickerType.TTS"
					:search="searchQuery"
					:data="filteredStickers"
					:loading="loading"
					@onPlaySound="actions.onPlaySound"
					@onStickerClick="events.onStickerClick"
					@onAddFavorite="events.onAddFavorite"
					@onDeleteFavorite="events.onDeleteFavorite" />
			</template>

			<StickersFooter
				:currentTabData="filteredStickers"
				:search="searchQuery"
				@onFirstPage="events.onFirstPage"
				@onPreviousPage="events.onPreviousPage"
				@onLastPage="events.onLastPage"
				@onNextPage="events.onNextPage" />
		</div>

		<!-- Dialogs -->
		<ErrorDialog
			v-if="dialogs.streamerOfflineDialog"
			:errorDialog="dialogs.streamerOfflineDialog"
			:sticker="clickedSticker"
			@onClose="dialogs.streamerOfflineDialog = false" />

		<CooldownDialog
			v-if="dialogs.coolDialog"
			:coolDialog="dialogs.coolDialog"
			:sticker="stickerToSlap"
			@onClose="events.onCooldownDialogClosed()" />

		<BroadcasterSlap
			v-if="dialogs.broadcasterSlapDialog"
			:slapDialog="dialogs.broadcasterSlapDialog"
			:sticker="stickerToSlap"
			@useSticker="events.onBroadcasterStickerUsed()"
			@onClose="dialogs.broadcasterSlapDialog = false" />

		<SoundDialog
			v-if="dialogs.soundDialog"
			:soundDialog="dialogs.soundDialog"
			:sticker="stickerToSlap"
			@useSticker="events.onBroadcasterStickerUsed()"
			@close="dialogs.soundDialog = false" />

		<SubscribeDialog
			v-if="dialogs.subscribeDialog"
			:subscribeDialog="dialogs.subscribeDialog"
			:sticker="stickerToSlap"
			@onClose="dialogs.subscribeDialog = false" />

		<BroadcasterSuccessDialog
			v-if="dialogs.broadcasterSuccessDialog"
			:successDialog="dialogs.broadcasterSuccessDialog"
			:sticker="stickerToSlap"
			@onClose="dialogs.broadcasterSuccessDialog = false" />

		<LocationDialog
			v-if="dialogs.locationDialog"
			:locationDialog="dialogs.locationDialog"
			:isBroadcaster="isBroadcaster"
			:sticker="clickedSticker"
			@onUseSticker="events.onCustomStickerPlacement"
			@onClose="dialogs.locationDialog = false" />

		<ExtensionOffline
			v-if="dialogs.offlineDialog"
			:errorDialog="dialogs.offlineDialog"
			@onClose="dialogs.offlineDialog = false" />

		<UserIdAccessDialog
			v-if="dialogs.userIdDialog"
			:userIdDialog="dialogs.userIdDialog"
			@onClose="dialogs.userIdDialog = false"></UserIdAccessDialog>

		<ErrorMessageDialog
			v-if="dialogs.errorMsgDialog"
			:errorMsgDialog="dialogs.errorMsgDialog"
			:errorMessage="globalErrorMessage"
			@onClose="dialogs.errorMsgDialog = false"
			@onReloadExt="actions.reloadPanel"></ErrorMessageDialog>

		<MustBeLoggedInDialog
			v-if="dialogs.loginDialog"
			:loginDialog="dialogs.loginDialog"
			@onClose="dialogs.loginDialog = false"></MustBeLoggedInDialog>
	</div>
</template>

<script>
import { computed, reactive, ref, watch, onMounted } from 'vue';
import { useStore } from '@/store';
import { StickerType } from '../../utils/constants';

import { StickersTabData } from './StickersTabData';
import { StickersTabStreamerStatus } from './StickersTabStreamerStatus';
import { StickersTabShowSticker } from './StickersTabShowSticker';

import SubNav from '@/components/panel/Stickers/SubNav.vue';
import CategoryDropdown from '@/components/panel/Stickers/CategoryDropdown.vue';
import SearchBox from '@/components/panel/Stickers/SearchBox.vue';
import Stickers from '@/components/panel/Stickers/Stickers.vue';
import StickersFooter from '@/components/panel/Stickers/Footer.vue';

import ErrorDialog from '@/views/panels/ErrorDialog.vue';
import BroadcasterSlap from '@/views/panels/BroadcasterSlap.vue';
import LocationDialog from '@/views/panels/LocationDialog.vue';
import SubscribeDialog from '@/views/panels/SubscribeDialog.vue';
import SoundDialog from '@/views/panels/SoundDialog.vue';
import CooldownDialog from '@/views/panels/CooldownDialog.vue';
import BroadcasterSuccessDialog from '@/views/panels/BroadcasterSuccessDialog.vue';
import ExtensionOffline from '@/views/panels/ExtensionOffline.vue';
import UserIdAccessDialog from '@/views/panels/UserIdAccessDialog.vue';
import MustBeLoggedInDialog from '@/views/panels/MustBeLoggedInDialog.vue';
import ErrorMessageDialog from '@/views/panels/ErrorMessageDialog.vue';

export default {
	name: 'StickersTab',
	components: {
		CategoryDropdown,
		SubNav,
		SearchBox,
		Stickers,
		StickersFooter,
		ErrorDialog,
		CooldownDialog,
		BroadcasterSlap,
		LocationDialog,
		SubscribeDialog,
		SoundDialog,
		BroadcasterSuccessDialog,
		ExtensionOffline,
		UserIdAccessDialog,
		MustBeLoggedInDialog,
		ErrorMessageDialog,
	},
	setup() {
		const store = useStore();
		const stickerTab = ref(0);
		const loading = ref(false);
		const isSlapable = ref(false);
		const isSlapInProgress = ref(false);

		const searchQuery = ref('');
		const resetSearch = ref(false);

		let scrollPosition = 0;

		const clickedSticker = ref(null);

		// Show Loading Sheet
		const isPanelLoaded = computed(() => {
			return store.state.isPanelLoaded;
		});

		if (!isPanelLoaded.value) {
			store.commit('setPanelLoading', true);
		}

		const isMobile = computed(() => store.state.isMobile);

		const tabView = computed(() => store.state.tabView);

		const activeTabs = computed(() => store.getters.activeTabItems);

		const isPartyModeOn = computed(() => store.state.isPartyModeOn);

		const uiData = reactive(new StickersTabData());
		const streamerStatus = reactive(new StickersTabStreamerStatus(uiData));
		const showStickerHandler = reactive(new StickersTabShowSticker());

		const isBroadcaster = computed(() => store.state.role == 'broadcaster');
		const isExtensionOnline = computed(() => store.state.isExtensionOnline);
		const isStreamerOnline = computed(() => store.state.isStreamerOnline);
		const isSubscriber = computed(() => store.state.isSubscriber);

		// See https://dev.twitch.tv/docs/extensions/#identity for identity details.
		const isLoggedIn = computed(() => store.state.opaqueUserId && store.state.opaqueUserId.startsWith('U'));
		const hasUserIdAccess = computed(() => store.state.userId);

		const isScrolling = computed(() => {
			const size = store.state.size;

			if (size === 'sm' && filteredStickers.value.items.length > 4) {
				return true;
			}

			if (size === 'md' && filteredStickers.value.items.length > 6) {
				return true;
			}

			return false;
		});

		const dialogs = reactive({
			streamerOfflineDialog: false,
			broadcasterSlapDialog: false,
			subscribeDialog: false,
			broadcasterSuccessDialog: false,
			offlineDialog: false,
			coolDialog: false,
			userIdDialog: false,
			loginDialog: false,
			errorMsgDialog: false,
			locationDialog: false,
			soundDialog: false,
		});

		onMounted(() => {
			scrollPosition = window.scrollY;
			// Show login dialog if the user is not logged in.
			dialogs.loginDialog = !isLoggedIn.value;

			// Show user id dialog if we are logged in but do not have a user id.
			dialogs.userIdDialog = isLoggedIn.value && !hasUserIdAccess.value;

			window.scrollTo(0, scrollPosition);
		});

		// Watch for changes to user id access (e.g. grant / revoke).
		watch(hasUserIdAccess, () => {
			dialogs.userIdDialog = !hasUserIdAccess.value;
		});

		// Display a global error message, such as a TTS server failure.
		const globalErrorMessage = ref(null);
		watch(
			() => store.state.globalErrorMessage,
			() => {
				if (store.state.globalErrorMessage) {
					actions.preventAutoScroll();
					globalErrorMessage.value = store.state.globalErrorMessage;
					dialogs.errorMsgDialog = true;
					store.commit('setGlobalErrorMessage', null);
				}
			},
		);

		watch(dialogs, (newVal, oldVal) => {
			scrollPosition = window.scrollY;
			window.scrollTo(0, scrollPosition);
		});

		watch(
			dialogs,
			(newVal, oldVal) => {
				scrollPosition = window.scrollY;

				setTimeout(() => {
					checkDialog();
				}, 2);
			},
			{ deep: true },
		);

		const checkDialog = () => {
			const element = document.getElementsByClassName('v-dialog__content--active')[0];
			if (element) {
				window.scrollTo(0, scrollPosition);
				element.blur();
			} else {
				console.warn('Not found...');
			}
		};

		// If the streamer is offline (e.g. not running OBS), we make a call
		// every X seconds to see if the streamer has come online. When online,
		// the offline dialogs are hidden and sticker slaps are allowed.
		const statusCheckResult = function (result) {
			actions.preventAutoScroll();
			if (!isExtensionOnline.value) {
				dialogs.streamerOfflineDialog = false;

				// Only show dialogs to viewers if they have slapped a sticker.
				if (isBroadcaster.value || isSlapInProgress.value) {
					dialogs.offlineDialog = true;
				}

				// Schedule a status check, if broadcaster.
				if (isBroadcaster.value) {
					streamerStatus.scheduleStatusCheck(statusCheckResult);
				}
				return false;
			} else if (!isStreamerOnline.value) {
				dialogs.offlineDialog = false;

				// Only show dialogs to viewers if they have slapped a sticker.
				if (isBroadcaster.value || isSlapInProgress.value) {
					dialogs.streamerOfflineDialog = true;
				}

				// Schedule a status check, if broadcaster.
				if (isBroadcaster.value) {
					streamerStatus.scheduleStatusCheck(statusCheckResult);
				}
				return false;
			}

			dialogs.offlineDialog = false;
			dialogs.streamerOfflineDialog = false;
			if (result.doReloadServerData) {
				actions.fetchData();
				return false;
			}

			return true;
		};

		const events = {
			// Event passed up from the Sticker component.
			onStickerClick(sticker) {
				actions.preventAutoScroll();

				clickedSticker.value = { ...sticker };
				if (isSlapable.value) {
					// Double-click gate.
					isSlapable.value = false;
					setTimeout(() => {
						isSlapable.value = true;
					}, 1000);

					// Execute status check to ensure streamer is online and also party mode status.
					isSlapInProgress.value = true;

					streamerStatus.checkStatus(function (result) {
						if (statusCheckResult(result)) {
							isSlapInProgress.value = false;

							// Set sticker.
							Object.assign(showStickerHandler.stickerToSlap, sticker);
							//console.log(
							//	`Sticker clicked:\n${JSON.stringify(showStickerHandler.stickerToSlap, null, 4)}`,
							//);

							// Check sub-only sticker.
							if (sticker.subOnly && !isBroadcaster.value && !isSubscriber.value) {
								dialogs.subscribeDialog = true;
								return;
							}

							// Do other checks and show sticker.
							if (!isPartyModeOn.value) {
								// If TTS sticker, see if we just did a status check only before showing the TTS text dialog.
								// See store index.js for flow details.
								if (sticker.stickerType == StickerType.TTS && store.state.isDoTtsStatusCheckOnClick) {
									store.commit('setIsTtsStatusCheckSuccess', true);
									store.commit('setClickedTtsSticker', { ...sticker });

									return;
								}

								if (isBroadcaster.value) {
									if (sticker.bits > 0) {
										dialogs.broadcasterSlapDialog = true;
									} else {
										actions.showSticker();
									}
								} else {
									actions.showSticker();
								}
							} else {
								actions.showSticker();
							}
						} else {
							isSlapInProgress.value = false;
						}
					}, sticker);
				}
			},

			// Broadcaster has chosen to use the sticker from the dialog.
			onBroadcasterStickerUsed() {
				dialogs.broadcasterSlapDialog = false;
				dialogs.soundDialog = false;
				actions.showSticker();
			},

			// The custom sticker placement dialog was dismissed with placement values passed.
			onCustomStickerPlacement: function (slap) {
				showStickerHandler.stickerToSlap.customPlacement = slap;
				actions.showSticker(true);
			},

			// The cooldown dialog was dismissed after a sticker show.
			onCooldownDialogClosed() {
				dialogs.coolDialog = false;
			},

			// Pagination.
			onFirstPage() {
				const data = searchQuery.value ? filteredStickers.value : currentTabData.value;
				if (data.currentPage != 0) {
					data.currentPage = 0;
					store.commit('setCurrentPage', data.currentPage);
				} else {
					actions.switchPrevTab();
				}
			},
			onPreviousPage() {
				const data = searchQuery.value ? filteredStickers.value : currentTabData.value;
				if (data.currentPage > 0) {
					data.currentPage--;
					store.commit('setCurrentPage', data.currentPage);
				} else {
					actions.switchPrevTab();
				}
			},
			onLastPage() {
				const data = searchQuery.value ? filteredStickers.value : currentTabData.value;
				if (data.currentPage < data.totalPages - 1) {
					data.currentPage = data.totalPages - 1;
					store.commit('setCurrentPage', data.currentPage);
				} else {
					actions.switchNextTab();
				}
			},
			onNextPage() {
				const data = searchQuery.value ? filteredStickers.value : currentTabData.value;

				if (data.currentPage < data.totalPages - 1) {
					data.currentPage++;
					store.commit('setCurrentPage', data.currentPage);
				} else {
					actions.switchNextTab();
				}
			},

			onAddFavorite(data) {
				uiData.addFavorite(data);
			},
			onDeleteFavorite(data) {
				uiData.deleteFavorite(data);
			},
		};

		const actions = {
			onSearch(val) {
				searchQuery.value = val;
				resetSearch.value = false;
				store.commit('setCurrentPage', 0);
			},

			onPlaySound(sticker) {
				Object.assign(showStickerHandler.stickerToSlap, sticker);
				dialogs.soundDialog = true;
			},

			// Prevent Auto Scroll
			preventAutoScroll() {
				if ('scrollRestoration' in window.history) {
					window.history.scrollRestoration = 'manual';
				}
			},

			setTab(tab) {
				// resetSearch.value = true;
				if (tab !== null && tab >= 0) {
					stickerTab.value = tab;
					const activeTab = activeTabs.value[tab];
					uiData.setTabAsClicked(activeTab.type);

					const data = searchQuery.value ? filteredStickers.value : currentTabData.value;
					store.commit('setCurrentPage', data.currentPage);
				}
			},

			switchNextTab() {
				// resetSearch.value = true;

				const data = searchQuery.value ? filteredStickers.value : currentTabData.value;
				const totalTabs = activeTabs.value.length;

				store.commit('setCurrentPage', 0);

				if (data.currentPage === data.totalPages - 1 || data.totalPages === 0) {
					if (stickerTab.value < totalTabs - 1) {
						const nextTab = stickerTab.value + 1;
						actions.setTab(nextTab);

						// Set Current Page of updated tab
						data.currentPage = 0;
						store.commit('setCurrentPage', data.currentPage);

						return;
					}

					if (stickerTab.value === totalTabs - 1) {
						const nextTab = 0;
						actions.setTab(nextTab);

						// Set Current Page of updated tab
						const updatedData = searchQuery.value ? filteredStickers.value : currentTabData.value;
						if (stickerTab.value === 0) {
							updatedData.currentPage = 0;
							store.commit('setCurrentPage', 0);
						}
					}
				}
			},

			switchPrevTab() {
				// resetSearch.value = true;

				const data = searchQuery.value ? filteredStickers.value : currentTabData.value;
				const totalTabs = activeTabs.value.length;

				if (data.currentPage === 0 || data.totalPages === 0) {
					if (stickerTab.value > 0) {
						const nextTab = stickerTab.value - 1;
						actions.setTab(nextTab);
						const updatedData = searchQuery.value ? filteredStickers.value : currentTabData.value;
						if (updatedData.totalPages > 0) {
							updatedData.currentPage = updatedData.totalPages - 1;
						} else {
							updatedData.currentPage = 0;
						}
						store.commit('setCurrentPage', updatedData.currentPage);
					} else {
						const nextTab = totalTabs - 1;
						actions.setTab(nextTab);
					}

					// Set Current Page of updated tab
					const updatedData = searchQuery.value ? filteredStickers.value : currentTabData.value;
					if (updatedData.totalPages > 0) {
						updatedData.currentPage = updatedData.totalPages - 1;
					} else {
						updatedData.currentPage = 0;
					}
					store.commit('setCurrentPage', updatedData.currentPage);
				}
			},

			// Show the sticker.
			showSticker(isStickerPlacementComplete) {
				const sticker = showStickerHandler.stickerToSlap;

				// Check if we have already shown the sticker placement dialog.
				// We do not display the dialog during party mode.
				if (!isPartyModeOn.value) {
					if (!isStickerPlacementComplete) {
						if (sticker.isStickerPlacementEnabled) {
							dialogs.locationDialog = true;
							return;
						}
					}
				}

				const onComplete = function () {
					actions.preventAutoScroll();
					if (isBroadcaster.value) {
						dialogs.broadcasterSuccessDialog = true;
					} else {
						dialogs.coolDialog = true;
					}
				};

				const onBitsUsed = function () {
					uiData.onAfterAfterBitsUsed(sticker.bits);
				};

				showStickerHandler.showSticker(isBroadcaster.value, onComplete, onBitsUsed);
			},

			fetchData: function () {
				actions.preventAutoScroll();

				isSlapable.value = false;
				loading.value = true;

				uiData.loadDataFromServer(function () {
					loading.value = false;
					isSlapable.value = true;

					if (isBroadcaster.value && (!isExtensionOnline.value || !isStreamerOnline.value)) {
						streamerStatus.checkStatus(statusCheckResult);
					}
				});
			},

			loadDataFromStore: function (panelServerResponse) {
				isSlapable.value = false;
				loading.value = true;

				uiData.loadDataFromStore(panelServerResponse, function () {
					loading.value = false;
					isSlapable.value = true;
				});
			},

			reloadPanel() {
				window.location.reload();
			},
		};

		const currentTabData = computed(() => {
			const selectedTab = store.getters.activeTabItems[stickerTab.value];
			if (!selectedTab.type) {
				return uiData.data.allStickers;
			} else if (selectedTab.type == StickerType.FAVORITE) {
				return uiData.favoriteStickers; // Computed value.
			} else if (selectedTab.type == StickerType.POWER) {
				return uiData.data.powerStickers;
			} else if (selectedTab.type == StickerType.CUSTOM) {
				return uiData.data.customStickers;
			} else if (selectedTab.type == StickerType.REWARD) {
				return uiData.data.rewardStickers;
			} else if (selectedTab.type == StickerType.TTS) {
				return uiData.data.ttsStickers;
			} else if (selectedTab.type == StickerType.SUB_ONLY) {
				return uiData.data.subOnlyStickers;
			} else if (selectedTab.type == StickerType.PLACEMENT) {
				return uiData.data.placementStickers;
			} else {
				throw new Error(`Invalid tab value ${stickerTab.value}.`);
			}
		});

		// Load initial data. We don't want to fetch data everytime the tab loads,
		// since it may result in many calls if viewers are clicking on the leaderboard
		// tab and back.
		const panelServerResponse = computed(() => {
			return store.state.panelServerResponse;
		});

		if (panelServerResponse.value) {
			actions.loadDataFromStore(panelServerResponse.value);
		} else {
			actions.fetchData();
		}

		const filteredStickers = computed(() => {
			let stickers = null;
			stickers = JSON.parse(JSON.stringify(currentTabData.value));

			if (searchQuery.value) {
				const items = stickers.items.filter(item => {
					// Search on name first.
					if (item.name.toLowerCase().includes(searchQuery.value.toLowerCase())) {
						return true;
					}
					// Then seach on terms.
					if (item.searchTerms && item.searchTerms.length > 0) {
						for (let i = 0; i < item.searchTerms.length; i++) {
							if (item.searchTerms[i].toLowerCase().includes(searchQuery.value.toLowerCase())) {
								return true;
							}
						}
					}
					return false;
				});
				const totalPages = Math.ceil(items.length / stickers.perPage);
				const totalItems = items.length;
				const currentPage = 0;

				stickers = { ...stickers, items, totalPages, totalItems, currentPage };
			}

			return stickers;
		});

		// Watch for changes to user id access (e.g. grant / revoke).
		const userId = computed(() => store.state.userId);
		watch(userId, () => {
			actions.fetchData();
		});

		// Watch Tab to reset Search
		watch(
			() => stickerTab.value,
			() => {
				// searchQuery.value = '';
				// resetSearch.value = true;
			},
		);

		return {
			isMobile,
			tabView,
			loading,
			isScrolling,
			resetSearch,
			searchQuery,
			dialogs,
			clickedSticker,
			uiData,
			data: uiData.data,
			stickerToSlap: showStickerHandler.stickerToSlap,
			actions,
			events,
			currentTabData,
			filteredStickers,
			stickerTab,
			isBroadcaster,
			globalErrorMessage,
			StickerType,
		};
	},
};
</script>
